'use client';
import React, { useState, useEffect, useRef } from 'react';

const BASE = 'https://cullertons-b4f8396.public.builtwithrocket.new';

// ─── Header ──────────────────────────────────────────────────────────────────
function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-cream/90 backdrop-blur-sm shadow-sm py-3' : 'bg-transparent py-5'}`
        }>

        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <a className="flex items-center gap-3 group" href="#">
            <div className="flex items-center gap-2 py-1">
              <img
                alt="Logo"
                width={120}
                height={120}
                className="flex-shrink-0"
                src="/assets/images/Full_logo-1776845280095.png" />

            </div>
          </a>
          <nav className="hidden md:flex items-center gap-10">
            <a href="#about" className="text-xs uppercase tracking-[0.18em] font-medium transition-colors text-forest/60 hover:text-forest">ABOUT STEPHEN</a>
            <a href="#locality" className="text-xs uppercase tracking-[0.18em] font-medium transition-colors text-forest/60 hover:text-forest">THE NEW TOWN</a>
            <a href="#approach" className="text-xs uppercase tracking-[0.18em] font-medium transition-colors text-forest/60 hover:text-forest">Our Approach</a>
          </nav>
          <a href="#valuation" className="hidden md:inline-flex items-center gap-2 bg-forest text-cream text-xs uppercase tracking-[0.18em] font-semibold px-5 py-3 rounded-full hover:bg-sage transition-colors">
            Free Valuation
          </a>
          <button
            className="md:hidden text-forest"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}>

            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <line x1="3" y1="7" x2="21" y2="7" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="17" x2="17" y2="17" />
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Nav */}
      <div
        id="mobile-nav"
        className={`fixed inset-0 bg-cream z-[60] flex flex-col justify-center items-center md:hidden ${
        mobileOpen ? 'open' : 'closed'}`
        }>

        <button
          className="absolute top-5 right-6 text-forest text-3xl"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}>

          ×
        </button>
        <nav className="flex flex-col gap-8 text-center">
          <a href="#about" onClick={() => setMobileOpen(false)} className="font-serif text-3xl text-forest/70 hover:text-forest transition-colors">About Mark</a>
          <a href="#locality" onClick={() => setMobileOpen(false)} className="font-serif text-3xl text-forest/70 hover:text-forest transition-colors">Blackhall</a>
          <a href="#approach" onClick={() => setMobileOpen(false)} className="font-serif text-3xl text-forest/70 hover:text-forest transition-colors">Our Approach</a>
          <a href="#valuation" onClick={() => setMobileOpen(false)} className="mt-4 inline-flex justify-center items-center bg-forest text-cream text-sm uppercase tracking-[0.18em] font-semibold px-8 py-4 rounded-full">
            Free Valuation
          </a>
        </nav>
      </div>
    </>);

}

// ─── Hero Section ─────────────────────────────────────────────────────────────
function HeroSection() {
  const [activeSlide, setActiveSlide] = useState(0);
  const slides = [
  {
    src: `/assets/images/George_Street_New_Town_WEB-1776852189792.jpg`,
    alt: 'George Street in Edinburgh New Town with Georgian architecture and bustling street scene'
  },
  {
    src: `/assets/images/Georgian_New_Town_close-up_WEB-1776852328926.jpg`,
    alt: 'Close-up of Georgian architecture in Edinburgh New Town showing ornate stonework and details'
  },
  {
    src: `/assets/images/New_Town_drone_dusk_image-1776851827125.jpg`,
    alt: "Aerial drone view of Edinburgh\'s New Town at dusk with city lights and skyline"
  }];


  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides?.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides?.length]);

  return (
    <section className="relative min-h-screen overflow-hidden" aria-label="Hero">
      {/* Carousel */}
      <div className="absolute inset-0 z-0">
        {slides?.map((slide, i) =>
        <div key={i} className={`carousel-slide ${i === activeSlide ? 'active' : ''}`}>
            <div className="relative" style={{ width: '100%', height: '100%' }}>
              <img
              alt={slide?.alt}
              src={slide?.src}
              className="object-cover"
              style={{ position: 'absolute', height: '100%', width: '100%', left: 0, top: 0, right: 0, bottom: 0, objectFit: 'cover' }} />

            </div>
          </div>
        )}
      </div>
      {/* Overlays */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-forest/60 via-forest/30 to-forest/80 pointer-events-none" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-forest/40 to-transparent pointer-events-none" />
      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 pt-40 pb-20 min-h-screen flex flex-col justify-between">
        <div className="flex-1 flex flex-col justify-end pb-16 max-w-4xl">
          <h1
            className="font-serif text-cream leading-[0.92] tracking-tight mb-6 reveal-active"
            style={{ fontSize: 'clamp(3rem, 8vw, 7.5rem)' }}>

            <span className="text-reveal-wrapper block">
              <span className="text-reveal-content">We Know</span>
            </span>
            <span className="text-reveal-wrapper block">
              <span className="text-reveal-content delay-100 italic text-gold">the New Town.</span>
            </span>
            <span className="text-reveal-wrapper block">
              <span className="delay-200" style={{ color: 'rgba(255,255,255,0.8)' }}>I Live Here.</span>
            </span>
          </h1>
          <p className="text-cream/75 text-lg md:text-xl max-w-xl leading-relaxed reveal active font-normal">Renowned for exceptional client service, Stephen Mackenzie is a multi-award winning agent and director at Cullerton's, with over 27+ years experience in prime property sales, and a proud New Town resident.

          </p>
          <div className="flex flex-wrap gap-4 mt-8 reveal active delay-200">
            <a
              href="#about"
              className="inline-flex items-center gap-2 bg-white text-[#3F5948] text-sm uppercase tracking-[0.18em] font-semibold px-7 py-4 rounded-full hover:bg-cream transition-colors">MEET STEPHEN


            </a>
          </div>
        </div>
      </div>
    </section>);

}

export { Header, HeroSection };